import "./index.scss";
import "../../locales/i18n";

import { useMutation } from "@apollo/client";
import { useHistory } from "react-router-dom";
import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import Scrollbars from "react-custom-scrollbars";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { SimpleDropdown } from "@/components/components/Dropdown";
import { MessageType } from "@/components/enums";
import FormBody from "@/messenger/components/FormBody";
import FormFooter from "@/messenger/components/FormFooter";
import FormHeader from "@/messenger/components/FormHeader";
import { representativesSelectors } from "@/messenger/ducks/entities/representatives";
import { representativeSettingsSelectors } from "@/messenger/ducks/entities/representativeSettings";
import { addNotification, removeNotification } from "@/messenger/ducks/general/notifications.duck";
import { assignRepresentativeToContact } from "@/messenger/graphql/mutations";
import { TContact } from "@/messenger/types/entities/conversation";
import { TRepresentative } from "@/messenger/types/entities/representative";
import { assignContactValidation, getOptionsForReps } from "@/messenger/utils/helpers";
import { CHAT } from "@/messenger/constants/routing";
import { removeConversationAC } from "@/messenger/ducks/entities/conversations";
import { activeConversationSelector } from "@/messenger/selectors";
import { detectMobile } from "@/messenger/utils/deviceType";
import { deviceTypeSelector } from "@/messenger/ducks/selectors/general";
import { getRepresentativeIdOption } from "@/messenger/utils/conversation.helper";


interface TTextkitAssignContactProps {
    contact: TContact;
    onClose: () => void;
}

const TextKitAssignContact = ({ contact, onClose }: TTextkitAssignContactProps) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const history = useHistory();
    const deviceType = useSelector(deviceTypeSelector);
    const isMobile = detectMobile(deviceType);

    const [option, setOption] = useState([]);
    const representatives = useSelector(representativesSelectors.selectAll);

    const representativeSettings: TRepresentative[] = useSelector(representativeSettingsSelectors.selectAll);
    const currentUser = representativeSettings[0];
    const [representativeId, setRepresentativeId] = useState<string>(currentUser.participantId);

    const activeConversation = useSelector(activeConversationSelector)

    const representativeOptions = getOptionsForReps(representatives);

    useEffect(() => {
        setOption(representativeOptions.sort((a, b) => a.heading.localeCompare(b.heading)));
    }, []);

    const removeConversationCallback = (id: string, activeConvoId: string, nextConvoId: string) => {
        if (id === activeConvoId) {
            if (nextConvoId) {
                //history.push(`/${CHAT}/${nextConvoId}`);
                history.push(`/${CHAT}`);
            } else {
                history.push(`/${CHAT}`);
            }
        }
    };

    const { values, errors, handleSubmit, setFieldValue, dirty, isValid, touched, resetForm, setFieldError } = useFormik({
        initialValues: {
            contactId: contact.id,
            last_message: "2022-07-18",
            representativeId: getRepresentativeIdOption(activeConversation, representatives),
        },
        validationSchema: assignContactValidation,
        onSubmit: () => {
            if (isValid) {
                dispatch(removeConversationAC({ conversationId: activeConversation.id }, removeConversationCallback));
                assignRepresentativeToContactMutation({
                    variables: {
                        conversationId: activeConversation.id,
                        participantId: representativeId
                    },
                });
            }
        },
    });

    const [assignRepresentativeToContactMutation] = useMutation(assignRepresentativeToContact, {
        onCompleted: () => {
            const notification = {
                id: `${contact.id}_reassigned`,
                type: MessageType.NEUTRAL,
                message: "Contact Reassigned",
            };

            dispatch(addNotification(notification));
            clearForm();
            onClose();
            setTimeout(() => dispatch(removeNotification(`${contact.id}_reassigned`)), 3000);
        },
    });

    const clearForm = () => {
        resetForm();
        setFieldValue("topic", "Message");
        setFieldValue("last_message", "2023-06-07");

        setFieldError("last_message", undefined);
        setFieldError("topic", undefined);
    };

    const handleCancel = () => {
        clearForm();
        onClose();
    };


    const handleSubmitForm = () => {
        handleSubmit();
    };



    return (
        <div className="c-textkit-edit-a-contact">
            <FormHeader
                name={"ASSIGN_CONTACT"}
                title={"Assign Contact"}
                onClose={onClose}
            />
            <Scrollbars hideTracksWhenNotNeeded={true}>
                <FormBody>
                    <div className="c-textkit-edit-a-contact__space"></div>

                    <div className="c-textkit-edit-a-contact__dropdown">
                        <div className="c-textkit-edit-a-contact__dropdown-title">
                            {t("Assign To")}
                        </div>
                        <SimpleDropdown
                            isMobileView={isMobile}
                            multiple={false}
                            className="c-first-login__dropdown"
                            dropdownDefaultRenderClassNames={{ header: "c-first-login__dropdown-header" }}
                            heading={values.representativeId}
                            onSelect={(opt) => {
                                setFieldValue("representativeId", opt.heading);
                                setRepresentativeId(opt.label);
                            }}
                            options={option}
                            search={false}
                        />
                    </div>
                    {touched.representativeId && errors.representativeId && (
                        <div className="c-textkit-edit-a-contact__error">{errors}</div>
                    )}
                    <div className="c-textkit-edit-a-contact__space"></div>
                </FormBody>

                <FormFooter
                    name={"ASSIGN_CONTACT"}
                    onCancel={handleCancel}
                    submitTitle={t("Save")}
                    submitDisabled={!isValid || !dirty}
                    onSubmit={handleSubmitForm}
                />
            </Scrollbars>
        </div>
    );
};


export default TextKitAssignContact;